import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { Button } from "components/ui/button";

function listLocalStorageKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      keys.push(key);
    }
  }
  return keys;
}

const ClearCachePage: FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"clearing" | "cleared" | "error">(
    "clearing"
  );
  const [clearedKeys, setClearedKeys] = useState<string[]>([]);

  useEffect(() => {
    try {
      const keys = listLocalStorageKeys();
      localStorage.clear();
      setClearedKeys(keys);
      setStatus("cleared");
    } catch (error) {
      console.error("Failed to clear localStorage", error);
      setStatus("error");
    }
  }, []);

  return (
    <div className="px-6 py-10 max-w-2xl mx-auto">
      <div className="flex flex-col items-start gap-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
            <Trash2 className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Clear Cache</h1>
        </div>

        {status === "clearing" && (
          <p className="flex items-center text-base text-gray-700">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Clearing saved browser data…
          </p>
        )}

        {status === "cleared" && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-2 text-base text-gray-700">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              <p>
                All Health Harbor data saved in this browser&apos;s local
                storage has been cleared, including persisted previous request
                information.
              </p>
            </div>
            {clearedKeys.length > 0 ? (
              <p className="text-sm text-gray-500">
                Removed: {clearedKeys.join(", ")}
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                No saved local storage data was found.
              </p>
            )}
          </div>
        )}

        {status === "error" && (
          <p className="text-base text-red-600">
            We couldn&apos;t clear browser storage. Try using your
            browser&apos;s site settings to clear data for healthharbor.co.
          </p>
        )}

        <Button
          onClick={() => navigate("/dashboard")}
          className="bg-brand hover:bg-brand-hover text-white"
        >
          Back to dashboard
        </Button>
      </div>
    </div>
  );
};

export default ClearCachePage;
